import { X, Mic, Command } from 'lucide-react';
import { voiceCommands, formatCommand } from '../../utils/voiceCommands';
import type { CommandType } from '../../types/voice';

interface VoiceCommandHelpProps {
  isOpen: boolean;
  onClose: () => void;
  language?: string;
}

const commandTypeLabels: Record<CommandType, { es: string; en: string; icon: string }> = {
  navigation: { es: 'Navegación', en: 'Navigation', icon: '🧭' },
  chat: { es: 'Chat', en: 'Chat', icon: '💬' },
  audio: { es: 'Audio', en: 'Audio', icon: '🔊' },
  avatar: { es: 'Avatar', en: 'Avatar', icon: '👤' },
  app: { es: 'Aplicación', en: 'Application', icon: '⚙️' },
  text: { es: 'Texto', en: 'Text', icon: '📝' },
};

export default function VoiceCommandHelp({ isOpen, onClose, language = 'es' }: VoiceCommandHelpProps) {
  if (!isOpen) return null;

  const groupedCommands = voiceCommands.reduce((acc, cmd) => {
    if (!acc[cmd.type]) acc[cmd.type] = [];
    acc[cmd.type].push(cmd);
    return acc;
  }, {} as Record<CommandType, typeof voiceCommands>);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-base-100 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-base-300">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <Mic className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-base-content">
                Comandos de Voz
              </h2>
              <p className="text-sm text-base-content/60">
                Controla la app con tu voz
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="btn btn-ghost btn-circle"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {Object.entries(groupedCommands).map(([type, commands]) => {
            const typeInfo = commandTypeLabels[type as CommandType];
            return (
              <div key={type}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">{typeInfo.icon}</span>
                  <h3 className="font-semibold text-base-content">
                    {language === 'es' ? typeInfo.es : typeInfo.en}
                  </h3>
                </div>
                <div className="grid gap-2">
                  {commands.map((cmd) => (
                    <div
                      key={cmd.id}
                      className="flex items-center justify-between p-3 bg-base-200 rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        <Command className="w-4 h-4 text-primary" />
                        <span className="font-medium text-base-content">
                          "{formatCommand(cmd, language)}"
                        </span>
                      </div>
                      {cmd.requiresConfirmation && (
                        <span className="text-xs text-warning bg-warning/10 px-2 py-1 rounded">
                          Requiere confirmación
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-base-300 bg-base-200/50 rounded-b-2xl">
          <p className="text-sm text-base-content/60 text-center">
            💡 <strong>Consejo:</strong> Puedes usar "Hey Avatar" o "Hola Avatar" antes de un comando
          </p>
        </div>
      </div>
    </div>
  );
}